import { useState } from "react";
import { toast } from "sonner";
import { UserX, RotateCcw, Search } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useFetch } from "../lib/hooks";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";
import { PageHeader, StatCard, StatusBadge, Skeleton } from "../components/common/Ui";
import { JourneyHint } from "../components/common/Journey";
import { DataTable } from "../components/common/DataTable";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

export default function Dropped() {
  const { t } = useI18n();
  const { user } = useAuth();
  const { data, reload } = useFetch("/students/dropped-list");
  const [q, setQ] = useState("");
  if (!data) return <div className="space-y-4"><Skeleton className="h-9 w-56" /><Skeleton className="h-64" /></div>;
  const rows = data.filter((r) => !q || `${r.name} ${r.nik} ${r.drop_reason}`.toLowerCase().includes(q.toLowerCase()));
  const undo = async (r) => {
    if (!window.confirm(`Batalkan DO ${r.name}? Siswa kembali aktif dan masuk rombel semula.`)) return;
    try { await api.post(`/students/${r.id}/dropout/undo`); toast.success(`${r.name} kembali aktif`); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const month = new Date().toISOString().slice(0, 7);
  return (
    <div>
      <PageHeader title="Siswa DO" subtitle="Siswa yang dikeluarkan dari Yutaka (Drop Out). Dihapus dari rombel, jadwal, kanban & penilaian; arsip data tetap tersimpan." />
      <JourneyHint k="dropped" />
      <div className="grid gap-4 sm:grid-cols-3 mb-6">
        <StatCard label="Total siswa DO" value={data.length} icon={UserX} tone="red" testId="dropped-total" />
        <StatCard label="DO bulan ini" value={data.filter((r) => (r.dropped_at || "").startsWith(month)).length} icon={UserX} tone="orange" testId="dropped-month" />
        <StatCard label="Alasan terbanyak" value={(() => { const c = {}; data.forEach((r) => { c[r.drop_reason] = (c[r.drop_reason] || 0) + 1; }); const top = Object.entries(c).sort((a, b) => b[1] - a[1])[0]; return top ? top[0] : "-"; })()} icon={UserX} tone="slate" testId="dropped-top-reason" />
      </div>
      <DataTable testId="dropped-table" rows={rows} exportName="siswa_do.csv" columns={[
        { key: "name", label: t("name"), render: (r) => <span><span className="font-medium">{r.name}</span><p className="text-xs text-slate-400 font-mono">{r.nik}</p></span> },
        { key: "batch_name", label: t("batch") },
        { key: "stage", label: "Kelas terakhir", render: (r) => <StatusBadge label={r.stage || "-"} tone="indigo" /> },
        { key: "group_name", label: "Rombel terakhir", render: (r) => r.group_name || "-" },
        { key: "dropped_at", label: "Tanggal DO", render: (r) => <span className="font-mono text-xs">{r.dropped_at?.slice(0, 10)}</span> },
        { key: "drop_reason", label: "Alasan", render: (r) => <span><span className="font-medium text-red-700">{r.drop_reason}</span>{r.drop_notes && <p className="text-xs text-slate-500">{r.drop_notes}</p>}</span> },
        { key: "dropped_by_name", label: "Oleh" },
        { key: "attitude_points", label: "Attitude" },
        ...(user.role === "super_admin" ? [{ key: "undo", label: "", render: (r) => <Button data-testid="dropout-undo-button" size="sm" variant="outline" onClick={() => undo(r)}><RotateCcw className="h-3.5 w-3.5 mr-1" />Batalkan DO</Button> }] : []),
      ]} toolbar={<div className="relative"><Search className="h-4 w-4 absolute left-2 top-2.5 text-slate-400" /><Input data-testid="dropped-search" className="pl-8 w-64" placeholder="Cari nama / NIK / alasan" value={q} onChange={(e) => setQ(e.target.value)} /></div>} />
    </div>
  );
}
